// ─── Friendly-URL Resolve API ───
// GET /api/v1/resolve — maps a friendly route ref's 6-hex short id back to a
// real, scope-checked entity (ADR 0012 D2, Phase 6.5 item 2). See
// packages/shared/src/schemas/resolve.schema.ts for the full wire contract and
// docs/adr/0012-friendly-urls.md for the route-loader flow this backs.

import type { ResolveResponse, ResolveType } from '@a8-planner/shared';
import { apiClient } from '../api-client';

export interface ResolveParams {
  /** Entity kind the ref addresses. */
  type: ResolveType;
  /** 6-hex trailing short id parsed from the ref (see parseEntityRef). */
  short: string;
  /** Cosmetic tie-breaker only (D1) — omit when resolving a bare short (alias routes). */
  slug?: string;
}

export const resolveApi = {
  /**
   * Resolves a friendly-URL ref to its real entity. 404s (out-of-scope or
   * nonexistent — identical by design, see the ADR's Security invariant) and
   * 409s (ambiguous short id — astronomically unlikely at this scale) surface
   * as `ApiError` for the caller (a route loader) to map.
   */
  resolve(params: ResolveParams): Promise<ResolveResponse> {
    const query = new URLSearchParams();
    query.set('type', params.type);
    query.set('short', params.short);
    if (params.slug) {
      query.set('slug', params.slug);
    }
    // suppressAuthRedirect: a resolve runs inside a route loader during
    // navigation. On a cold logged-out load its 401 must NOT trigger the
    // api-client's global hard `window.location` reload — entity-resolve
    // catches the 401 and throws a smooth in-SPA redirect to /login?returnTo
    // instead (the item-1 deep-link pattern; ADR 0012 D3 SSO sequence).
    return apiClient.get<ResolveResponse>(`/resolve?${query.toString()}`, {
      suppressAuthRedirect: true,
    });
  },
};
